import React, { useEffect, useState } from "react";
import "./css/Uebungsliste.css";

export default function Uebungsliste({ onAddToPlaner, ageColors }) {
  const [uebungen, setUebungen] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [excludedAges, setExcludedAges] = useState([]);
  const [excludedTools, setExcludedTools] = useState([]);

  useEffect(() => {
    fetch("/data/uebungen.json")
      .then((res) => res.json())
      .then((data) => setUebungen(data));
  }, []);

  const categories = [...new Set(uebungen.map(u => u.Kategorie))].sort();

  const filteredForOptions = selectedCategory
    ? uebungen.filter(u => u.Kategorie === selectedCategory)
    : uebungen;

  let ages = [
    ...new Set(
      filteredForOptions.flatMap(u => u.Altersklasse || [])
    )
  ].sort();

  // Letztes Element nach hinten verschieben (Original-Logik)
  let tmp = ages.shift();
  ages.push(tmp);

  const toolMap = {
    "Radball": ["Radball", "Radbälle"],
    "Tor": ["Tor"],
    "Matte": ["Matte"],
    "Pfeife": ["Pfeife"],
  };

  function getAvailableTools(uebungen) {
    const foundTools = new Set();

    uebungen.forEach(uebung => {
      const text = (uebung.Utensilien || "").toLowerCase();
      for (const [tool, variants] of Object.entries(toolMap)) {
        if (variants.some(v => text.includes(v.toLowerCase()))) {
          foundTools.add(tool);
        }
      }
    });

    return Array.from(foundTools).sort();
  }

  const availableTools = getAvailableTools(uebungen);

  const toggleExcluded = (value, type) => {
    if (type === "age") {
      setExcludedAges(prev =>
        prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]
      );
    } else if (type === "tool") {
      setExcludedTools(prev =>
        prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]
      );
    }
  };

  const visibleUebungen = uebungen.filter(u => {
    if (selectedCategory && u.Kategorie !== selectedCategory) return false;
    if ((u.Altersklasse || []).every(age => excludedAges.includes(age))) return false;
    if (excludedTools.some(tool =>
      (toolMap[tool] || [tool]).some(variant =>
        u.Utensilien?.toLowerCase().includes(variant.toLowerCase())
      )
    )) return false;

    return true;
  }).sort((a, b) => {
    const catCompare = (a.Kategorie || "").localeCompare(b.Kategorie || "", "de", { sensitivity: "base" });
    if (catCompare !== 0) return catCompare;
    return (a.Titel || "").localeCompare(b.Titel || "", "de", { sensitivity: "base" });
  });

  return (
    <div className="Uebungsliste">
      <h2 className="Uebungsliste-title">Übungssammlung</h2>

      {/* Filterbereich */}
      <div className="filters">
        {/* Kategorie Dropdown */}
        <select
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
        >
          <option value="">Alle Kategorien</option>
          {categories.map(cat => (
            <option key={cat} value={cat}>{cat}</option>
          ))}
        </select>
      </div>

      <div className="filters">
        {/* Altersklassen Filter */}
        <div className="filter-list">
          {ages.map(age => (
            <button
              key={age}
              className={(excludedAges.includes(age) ? "excluded " : "") + "altersklasse-" + age}
              onClick={() => toggleExcluded(age, "age")}
            >
              {age}
            </button>
          ))}
        </div>

        {/* Utensilien Filter */}
        <div className="filter-list">
          {availableTools.map(tool => (
            <button
              key={tool}
              className={excludedTools.includes(tool) ? "excluded" : ""}
              onClick={() => toggleExcluded(tool, "tool")}
            >
              {tool}
            </button>
          ))}
        </div>
      </div>

      {/* Übungskarten */}
      <div className="UebungsGrid">
        {visibleUebungen.map((uebung, index) => (
          <div
            className="UebungCard"
            key={index}
            onClick={() => onAddToPlaner && onAddToPlaner(uebung)}
            id={uebung.website}
          >
            <div className="altersklassen-badges">
              {(uebung.Altersklasse || [])
                .filter(age => !!age && age !== "NaN")
                .map((age, idx) => (
                  <span
                    key={idx}
                    style={{
                      backgroundColor: ageColors[age] || "#ccc"
                    }}
                  >
                    {age}
                  </span>
                ))}
            </div>
            <h2>{uebung.Titel}</h2>
            {uebung.Kategorie && <p><strong>Kategorie:</strong> {uebung.Kategorie}</p>}
            {uebung.Unterkategorie && <p><strong>Unterkategorie:</strong> {uebung.Unterkategorie}</p>}
            {uebung.Intensität && <p><strong>Intensität:</strong> {uebung.Intensität}</p>}
            {uebung.Utensilien && <p><strong>Utensilien:</strong> {uebung.Utensilien}</p>}
            {uebung.Ausgangsposition && <p><strong>Ausgangsposition:</strong> {uebung.Ausgangsposition}</p>}
            {Array.isArray(uebung.Ablauf) &&
              uebung.Ablauf.some(e => e && e !== "NaN") && (
              <div className="ablauf-box">
                <h4>📋 Ablauf</h4>
                <ul>
                  {uebung.Ablauf.filter(e => e && e !== "NaN").map((step, idx) => (
                    <li key={idx}>{step}</li>
                  ))}
                </ul>
              </div>
            )}
            {Array.isArray(uebung.Trainer) &&
              uebung.Trainer.some(e => e && e !== "NaN") && (
              <div className="trainer-box">
                <h4>🎯 Trainer</h4>
                <ul>
                  {uebung.Trainer.filter(e => e && e !== "NaN").map((tip, idx) => (
                    <li key={idx}>{tip}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
